import {
  hideValidationPopup,
  validationPopup,
} from "../redux/actions";
import { changeEmail } from "../services/UserService";
import {
  isEmailRegistered,
  verifyPasswordAndRequestEmailChange,
} from "../auth/authApi";
import { pocketBaseErrorMessage } from "../auth/errors";

export const validateEmailChangeForm = (formData, currentEmail, lang = "en") => {
  const errors = {};
  const requiredMessage =
    lang === "en" ? "This field is required" : "この項目は必須です";
  const invalidEmailMessage =
    lang === "en" ? "Invalid email format" : "無効なメール形式です";
  const sameMessage =
    lang === "en"
      ? "Enter a different email than your current one"
      : "現在のメールとは別のアドレスを入力してください";

  const raw = formData.email?.trim() || "";
  if (!raw) {
    errors.email = requiredMessage;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
    errors.email = invalidEmailMessage;
  } else if (
    currentEmail &&
    raw.toLowerCase() === String(currentEmail).trim().toLowerCase()
  ) {
    errors.email = sameMessage;
  }

  if (!formData.password?.trim()) {
    errors.password = requiredMessage;
  }

  return errors;
};

const dispatchValidationError = (dispatch, errors, lang) => {
  const errorMessage =
    Object.keys(errors).length > 1
      ? lang === "en"
        ? "Please complete all necessary fields."
        : "必須項目を入力してください。"
      : Object.values(errors)[0];

  dispatch(
    validationPopup({
      message: errorMessage,
      icon: "horror",
      type: "error",
    })
  );
};

function axiosErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    ""
  );
}

function axiosStatus(err) {
  return err?.response?.status ?? err?.status ?? 0;
}

function pocketBaseIsUniqueEmailError(err) {
  const code = err?.data?.data?.email?.code || err?.data?.email?.code;
  const msg =
    err?.data?.data?.email?.message ||
    err?.data?.email?.message ||
    err?.message ||
    "";
  return (
    code === "validation_not_unique" ||
    String(msg).toLowerCase().includes("unique") ||
    String(msg).toLowerCase().includes("already")
  );
}

/**
 * 1) PocketBase: verify password + requestEmailChange (confirmation link to the **new** address) — primary for PB-only hosts.
 * 2) Legacy `POST /profile/change-email` if PB fails (e.g. custom server that only implements the legacy route).
 */
export const submitEmailChangeFromSettings = async (
  dispatch,
  formData,
  currentEmail,
  lang = "en"
) => {
  const errors = validateEmailChangeForm(formData, currentEmail, lang);
  if (Object.keys(errors).length > 0) {
    dispatchValidationError(dispatch, errors, lang);
    return { success: false, errors };
  }

  dispatch(
    validationPopup({
      message: "Loading...",
      icon: "loading",
      type: "info",
    })
  );

  const newEmail = formData.email.trim().toLowerCase();
  const password = formData.password;
  const expiresAtMs = Date.now() + 15 * 60 * 1000;

  const legacySuccessMsg =
    lang === "en"
      ? "Request received. Check your current inbox — we sent a message there about this change."
      : "処理しました。現在のメールアドレス宛に通知を送りました。受信箱をご確認ください。";

  const pbSuccessMsg =
    lang === "en"
      ? "Password confirmed. Check your new inbox for a link to finish changing your email."
      : "パスワードを確認しました。新しいメールの受信箱で、変更完了用のリンクをご確認ください。";

  try {
    // Step 1: pre-check (best-effort) if email is already registered.
    // If this fails due to rules, we still proceed (PB will validate uniqueness anyway).
    try {
      const exists = await isEmailRegistered(newEmail);
      if (exists) {
        const msg =
          lang === "en"
            ? "This email is already registered. Please use a different email."
            : "このメールアドレスは既に登録されています。別のメールをお試しください。";
        dispatch(hideValidationPopup());
        dispatch(
          validationPopup({
            message: msg,
            icon: "horror",
            type: "error",
          })
        );
        return { success: false, errors: { email: msg } };
      }
    } catch {
      // ignore: treat as "unknown" availability
    }

    await verifyPasswordAndRequestEmailChange(password, newEmail);
    dispatch(hideValidationPopup());
    dispatch(
      validationPopup({
        message: pbSuccessMsg,
        icon: "like",
        type: "success",
      })
    );
    return { success: true, pendingEmail: newEmail, expiresAtMs };
  } catch (pbErr) {
    try {
      // If PB returned a uniqueness error, prefer a direct form error over legacy fallback.
      if (pocketBaseIsUniqueEmailError(pbErr)) {
        const msg =
          lang === "en"
            ? "This email is already registered. Please use a different email."
            : "このメールアドレスは既に登録されています。別のメールをお試しください。";
        dispatch(hideValidationPopup());
        dispatch(
          validationPopup({
            message: msg,
            icon: "horror",
            type: "error",
          })
        );
        return { success: false, errors: { email: msg } };
      }

      await changeEmail(newEmail, password, lang);
      dispatch(hideValidationPopup());
      dispatch(
        validationPopup({
          message: legacySuccessMsg,
          icon: "like",
          type: "success",
        })
      );
      return { success: true };
    } catch (legacyErr) {
      dispatch(hideValidationPopup());
      // If the legacy route doesn't exist (404), don't show that misleading error.
      // Surface the PocketBase error (usually wrong password / auth rules).
      if (axiosStatus(legacyErr) === 404) {
        const msg =
          pocketBaseErrorMessage(pbErr) ||
          (lang === "en"
            ? "Could not change email. Check your password and try again."
            : "メールを変更できませんでした。パスワードを確認してください。");
        dispatch(
          validationPopup({
            message: msg,
            icon: "horror",
            type: "error",
          })
        );
        return { success: false, errors: { submit: msg } };
      }

      const msg =
        axiosErrorMessage(legacyErr) ||
        pocketBaseErrorMessage(pbErr) ||
        (lang === "en"
          ? "Could not change email. Check your password and try again."
          : "メールを変更できませんでした。パスワードを確認してください。");
      dispatch(
        validationPopup({
          message: msg,
          icon: "horror",
          type: "error",
        })
      );
      return { success: false, errors: { submit: msg } };
    }
  }
};
